import { z } from 'zod';

// ─── Module / Agent / Tool ────────────────────────────────────────────────

export const toolSchema = z.object({
	name: z.string(),
	description: z.string(),
	callCount24h: z.number().int().nonnegative(),
});

export const agentFlowNodeSchema = z.object({
	name: z.string(),
	kind: z.enum(['pre', 'llm', 'tools', 'post', 'end']),
});

export const agentSchema = z.object({
	name: z.string(),
	model: z.string(),
	pattern: z.string(),
	description: z.string(),
	path: z.string(),
	tools: z.array(toolSchema),
	flow: z.array(agentFlowNodeSchema),
});

export const dependencyStatusSchema = z.enum(['up', 'degraded', 'down', 'disabled']);
export type DependencyStatus = z.infer<typeof dependencyStatusSchema>;

export const dependencyKindSchema = z.enum(['redis', 'postgres', 'qdrant', 'intercom', 'llm', 'incidents_db']);
export type DependencyKind = z.infer<typeof dependencyKindSchema>;

export const moduleDependencySchema = z.object({
	name: z.string(),
	kind: dependencyKindSchema,
	required: z.boolean(),
	status: dependencyStatusSchema,
	latencyMs: z.number().int().nullable().optional(),
	message: z.string().nullable().optional(),
	checkedAt: z.string(),
});

export const moduleHealthSchema = z.object({
	status: dependencyStatusSchema,
	dependencies: z.array(moduleDependencySchema),
	checkedAt: z.string(),
});

// ─── Dependencies view ────────────────────────────────────────────────────
// Platform-wide dependency view (GET /admin/dependencies): each dependency's
// probe verdict (reused from moduleDependencySchema) plus its resource/load
// metrics. Values are raw — formatting (bytes→MB/GB, thousands) is the UI's
// job. `unit` drives the formatter; `limit` (when present) renders a meter.
export const dependencyMetricUnitSchema = z.enum(['bytes', 'count', 'percent', 'ops']);
export type DependencyMetricUnit = z.infer<typeof dependencyMetricUnitSchema>;

export const dependencyMetricSchema = z.object({
	key: z.string(),
	label: z.string(),
	value: z.number().nullable().optional(),
	unit: dependencyMetricUnitSchema.nullable().optional(),
	limit: z.number().nullable().optional(),
	detail: z.string().nullable().optional(),
});

export const dependencyDetailSchema = moduleDependencySchema.extend({
	metrics: z.array(dependencyMetricSchema).default([]),
});

export const dependenciesResponseSchema = z.object({
	status: dependencyStatusSchema,
	dependencies: z.array(dependencyDetailSchema),
	checkedAt: z.string(),
});

export const moduleSchema = z.object({
	name: z.string(),
	description: z.string(),
	path: z.string(),
	status: z.enum(['live', 'shadow', 'disabled']),
	agents: z.array(agentSchema),
	costs: z.object({
		last24h: z.number().nonnegative(),
		last7d: z.number().nonnegative(),
		currentMonth: z.number().nonnegative(),
		monthProjection: z.number().nonnegative(),
		avgPerTurn: z.number().nonnegative(),
	}),
	turns: z.object({
		last24h: z.number().int().nonnegative(),
		errors24h: z.number().int().nonnegative(),
		activeThreads: z.number().int().nonnegative(),
		toolCalls24h: z.number().int().nonnegative(),
	}),
	health: moduleHealthSchema.nullable().optional(),
});

export type Tool = z.infer<typeof toolSchema>;
export type Agent = z.infer<typeof agentSchema>;
export type Module = z.infer<typeof moduleSchema>;
export type AgentFlowNode = z.infer<typeof agentFlowNodeSchema>;
export type ModuleDependency = z.infer<typeof moduleDependencySchema>;
export type ModuleHealth = z.infer<typeof moduleHealthSchema>;
export type DependencyMetric = z.infer<typeof dependencyMetricSchema>;
export type DependencyDetail = z.infer<typeof dependencyDetailSchema>;
export type DependenciesResponse = z.infer<typeof dependenciesResponseSchema>;

// ─── Conversation / Message ───────────────────────────────────────────────

export const messageRoleSchema = z.enum(['human', 'ai', 'tool', 'system']);
export type MessageRole = z.infer<typeof messageRoleSchema>;

export const conversationStatusSchema = z.enum(['active', 'handoff', 'closed', 'tool_error']);

/** How a conversation ended, from the per-turn decision behind the Intercom tags. */
export const conversationResultSchema = z.enum(['resolved', 'handoff', 'open']);
export type ConversationResult = z.infer<typeof conversationResultSchema>;
export type ConversationStatus = z.infer<typeof conversationStatusSchema>;

export const toolCallSchema = z.object({
	id: z.string(),
	name: z.string(),
	args: z.record(z.string(), z.unknown()),
	// The real tool result, correlated by `tool_call_id`. Null when no
	// matching `role='tool'` row exists.
	output: z.unknown().optional(),
	// Nullable on purpose, and null is the normal case rather than the edge:
	// the audit log writes every message of a turn in one transaction, so they
	// all share `created_at` and no duration can be derived; tool rows carry no
	// usage metadata either. Render `—`, never `0 ms · $0.000` — that reads as
	// a measurement. See back#SPEC-006.
	durationMs: z.number().int().nonnegative().nullish(),
	costUsd: z.number().nonnegative().nullish(),
	// The verdict the backend recorded, projected from the tool row's
	// `metadata.status` (back#SPEC-011). Null on rows written before that field
	// existed and on calls with no correlated tool row — absence of a
	// judgement, which is not the same as a pass.
	status: z.enum(['success', 'error']).nullish(),
	// Why it failed, when the backend knows. `type` separates blame:
	// `invalid_arguments` is the model's, `tool_exception` the tool's,
	// `upstream_http` the service behind it. It stays a plain string on purpose
	// — an error taxonomy grows, and a strict enum would make one unrecognised
	// value fail the whole payload and blank the page. Unknown types fall back
	// to 500, same principle as the alert chips.
	error: z.object({ type: z.string(), message: z.string() }).nullish(),
	// Real status code of the underlying service, only for tools that wrap a
	// network call. Null for in-process tools — synthesising a 200 would invent
	// a measurement. Filled by back#SPEC-011 slice 3, not yet shipped.
	statusCode: z.number().int().nullish(),
});

// Customer attachment (image or PDF). `url` is directly renderable: an S3
// presigned URL for fresh rows or a `data:` URI for legacy rows that still
// carry the base64 inline in the audit log.
export const messageAttachmentSchema = z.object({
	name: z.string(),
	contentType: z.string(),
	url: z.string(),
});

export const messageSchema = z.object({
	id: z.string(),
	role: messageRoleSchema,
	content: z.string(),
	createdAt: z.string(),
	authorName: z.string().nullish(),
	authorInitials: z.string().nullish(),
	model: z.string().nullish(),
	costUsd: z.number().nonnegative().nullish(),
	tokensIn: z.number().int().nonnegative().nullish(),
	tokensOut: z.number().int().nonnegative().nullish(),
	toolCalls: z.array(toolCallSchema).nullish(),
	attachments: z.array(messageAttachmentSchema).nullish(),
});

export const evaluationSummarySchema = z.object({
	status: z.enum(['pending', 'completed', 'not_applicable']),
	totalJudges: z.number().int().nonnegative(),
	passedJudges: z.number().int().nonnegative(),
	hasFailures: z.boolean(),
});
export type EvaluationSummary = z.infer<typeof evaluationSummarySchema>;

export const conversationListItemSchema = z.object({
	threadId: z.string(),
	userName: z.string(),
	userInitials: z.string(),
	companyName: z.string(),
	// The raw company id, shown under the name. It used to be projected *into*
	// `companyName`, so the column displayed an id labelled as a name and the
	// operator had no way to tell which was which. `.default('')` keeps older
	// payloads parsing.
	companyId: z.string().default(''),
	country: z.string(),
	channel: z.string(),
	messageCount: z.number().int().nonnegative(),
	costUsd: z.number().nonnegative(),
	lastActivity: z.string(),
	startedAt: z.string(),
	status: conversationStatusSchema,
	// Null for threads older than the per-turn log; it must not read as open.
	result: conversationResultSchema.nullish(),
	evaluationSummary: evaluationSummarySchema.nullish(),
	// Distinct RAG domains the conversation touched. Empty means the thread
	// never searched the KB *or* the classification event was dropped — the
	// backend emits them fire-and-forget — so render `—` and don't present it
	// as a complete picture. `.default([])` keeps older payloads parsing.
	consultationAreas: z.array(z.string()).default([]),
	// Triage alerts. An array because one conversation can accumulate several
	// (a `graph_error` and a `judge_failed` at once) and the operator needs to
	// know which. back#SPEC-007 will append `reported` without a contract change.
	alerts: z.array(z.string()).default([]),
});

export const conversationDetailSchema = conversationListItemSchema.extend({
	userId: z.string(),
	companyId: z.string(),
	moduleName: z.string(),
	agentName: z.string(),
	tokensIn: z.number().int().nonnegative(),
	tokensOut: z.number().int().nonnegative(),
	toolCallCount: z.number().int().nonnegative(),
	messages: z.array(messageSchema),
	rawState: z.record(z.string(), z.unknown()),
});

export type ToolCall = z.infer<typeof toolCallSchema>;
export type MessageAttachment = z.infer<typeof messageAttachmentSchema>;
export type Message = z.infer<typeof messageSchema>;
export type ConversationListItem = z.infer<typeof conversationListItemSchema>;
export type ConversationDetail = z.infer<typeof conversationDetailSchema>;

// ─── Metrics ──────────────────────────────────────────────────────────────

export const metricRangeSchema = z.enum(['24h', '7d', '30d']);
export type MetricRange = z.infer<typeof metricRangeSchema>;

export const metricDeltaDirectionSchema = z.enum(['up', 'down', 'flat']);
export type MetricDeltaDirection = z.infer<typeof metricDeltaDirectionSchema>;

export const metricStatSchema = z.object({
	value: z.number(),
	formatted: z.string(),
	deltaPct: z.number().nullable(),
	deltaDirection: metricDeltaDirectionSchema,
	deltaLabel: z.string(),
	spark: z.array(z.number()),
});

// `costUsd: null` means nothing could be priced; it must not read as 0.
export const layerCostSchema = z.object({
	component: z.string(),
	engines: z.array(z.string()).default([]),
	calls: z.number().int().nonnegative(),
	tokensIn: z.number().int().nonnegative(),
	tokensOut: z.number().int().nonnegative(),
	costUsd: z.number().nullable(),
});

export const modelCostSchema = z.object({
	model: z.string(),
	calls: z.number().int().nonnegative(),
	costUsd: z.number().nullable(),
});

export const measuredCostSchema = z.object({
	calls: z.number().int().nonnegative(),
	turns: z.number().int().nonnegative(),
	byLayer: z.array(layerCostSchema).default([]),
	byModel: z.array(modelCostSchema).default([]),
});

export const graphVersionBreakdownSchema = z.object({
	version: z.string(),
	turns: z.number().int().nonnegative(),
	p50Ms: z.number().nullable(),
	p95Ms: z.number().nullable(),
	costUsd: z.number().nullable(),
});

export type LayerCost = z.infer<typeof layerCostSchema>;
export type ModelCost = z.infer<typeof modelCostSchema>;
export type GraphVersionBreakdown = z.infer<typeof graphVersionBreakdownSchema>;

export const metricsSchema = z.object({
	range: metricRangeSchema,
	turns: metricStatSchema,
	errors: metricStatSchema,
	activeThreads: metricStatSchema,
	toolCalls: metricStatSchema,
	cost: metricStatSchema.extend({
		monthToDateFormatted: z.string(),
	}),
	toolUsage: z.array(
		z.object({
			name: z.string(),
			count: z.number().int().nonnegative(),
			percent: z.number().min(0).max(100),
		}),
	),
	turnsByDay: z.array(
		z.object({
			dayLabel: z.string(),
			count: z.number().int().nonnegative(),
			isToday: z.boolean(),
		}),
	),
	measuredCost: measuredCostSchema.nullish(),
	byGraphVersion: z.array(graphVersionBreakdownSchema).default([]),
});

export type MetricStat = z.infer<typeof metricStatSchema>;
export type Metrics = z.infer<typeof metricsSchema>;
